'use client'

import React, { useCallback, useEffect } from 'react'
import { Marker, useMap } from 'react-map-gl/maplibre'
import Supercluster, { ClusterProperties, PointFeature } from 'supercluster'
import useSupercluster from 'use-supercluster'
import FLogo from '@/public/logo/f-logo.svg'
import hotelTitle from '@/lib/helpers/hotelName'
import { Hotel } from '@/payload-types'
import HotelCard from '../common/HotelCard'
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog'

function Markers({ markers }: { markers: Hotel[] }) {
  const mapRef = useMap()

  const [bounds, setBounds] = React.useState<[number, number, number, number]>()
  const [zoom, setZoom] = React.useState(0)

  const markerType = markers.length > 1 ? 'simple' : 'logo'

  const points = markers
    .filter((marker) => typeof marker.lat === 'string' && typeof marker.lng === 'string')
    .map((marker) => {
      return {
        type: 'Feature' as const,
        properties: { cluster: false, ...marker },
        geometry: {
          type: 'Point' as const,
          coordinates: [parseFloat(marker.lng ?? '0'), parseFloat(marker.lat ?? '0')],
        },
      }
    })

  useEffect(() => {
    if (!mapRef.current) return

    const map = mapRef.current.getMap()

    const updateBounds = () => {
      setBounds(map.getBounds().toArray().flat() as [number, number, number, number])
      setZoom(Math.floor(map.getZoom()))
    }

    // Initial update
    updateBounds()

    // Listen to map changes
    map.on('move', updateBounds)
    map.on('zoom', updateBounds)

    return () => {
      map.off('move', updateBounds)
      map.off('zoom', updateBounds)
    }
  }, [mapRef])

  const { clusters, supercluster } = useSupercluster({
    points: points || [],
    zoom,
    bounds,
    options: { radius: 40, maxZoom: 20 },
  })

  return (
    <div>
      {(
        clusters as PointFeature<
          (Hotel & { cluster: false }) | (ClusterProperties & { cluster: true })
        >[]
      )
        .sort((a, b) => {
          // Render clusters last so they are on top of markers
          if (a.properties.cluster && !b.properties.cluster) return 1
          if (!a.properties.cluster && b.properties.cluster) return -1
          return 0
        })
        .map((cluster, i) => {
          return (
            <MarkerWrapper
              markerType={markerType}
              key={i}
              item={cluster}
              superCluster={supercluster!}
              mapRef={mapRef}
            />
          )
        })}
    </div>
  )
}

function MarkerWrapper({
  item,
  superCluster,
  mapRef,
  markerType,
}: {
  item: PointFeature<(Hotel & { cluster: false }) | (ClusterProperties & { cluster: true })>
  superCluster: Supercluster
  mapRef: ReturnType<typeof useMap>
  markerType: 'simple' | 'logo'
}) {
  const [longitude, latitude] = item.geometry.coordinates

  const [open, setOpen] = React.useState(false)

  const { cluster: isCluster } = item.properties

  const expansionZoom = item.id
    ? Math.min(superCluster.getClusterExpansionZoom(Number(item.id)), 20)
    : 0

  const handleFlyTo = useCallback(
    (zoom: number, lengdegrad?: number, breddegrad?: number, duration?: number) => {
      if (typeof lengdegrad === 'undefined' || typeof breddegrad === 'undefined') return

      if (mapRef.current) {
        mapRef.current.flyTo({
          center: [lengdegrad, breddegrad],
          zoom: zoom,
          duration: duration ? duration : 300,
        })
      }
    },
    [mapRef],
  )

  const city =
    !isCluster && typeof item.properties.city !== 'number' ? item.properties.city : undefined

  return (
    <>
      {open && !isCluster && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className='p-0 w-[calc(100vw_-_1rem)] min-[440px]:w-[400px] rounded-3xl'>
            <DialogTitle className='sr-only'>
              {hotelTitle(item.properties.title, item.properties.hotelType)}
            </DialogTitle>
            <HotelCard
              hotel={item.properties}
              city={{ slug: city?.slug ?? '', title: city?.title ?? '' }}
              country={city?.countrySlug ?? ''}
            />
          </DialogContent>
        </Dialog>
      )}
      <Marker longitude={longitude} latitude={latitude} anchor='bottom' className='z-10'>
        {isCluster ? (
          <button
            onClick={() =>
              handleFlyTo(expansionZoom, item.geometry.coordinates[0], item.geometry.coordinates[1])
            }
            className='rounded-full bg-black aspect-square relative z-10 w-10 border-2 flex items-center justify-center border-white'
          >
            <div className='text-primary-foreground text-lg font-medium'>
              {item.properties.point_count}
            </div>
          </button>
        ) : markerType === 'simple' ? (
          <button type='button' onClick={() => setOpen(!open)} className='relative block cursor-pointer'>
            <span className='rounded-full bg-black aspect-square size-7 border-[3px] relative flex items-center justify-center border-white' />
            <span className='block bg-white py-1 px-2 -translate-y-full -top-4 left-1/2 -translate-x-1/2 absolute rounded-md w-auto text-base font-medium min-w-[150px]'>
              <span className='block relative z-10 text-center text-balance line-clamp-2'>
                {hotelTitle(item.properties.title, item.properties.hotelType)}
              </span>
              <span className='block aspect-square bg-white w-5 rotate-45 -translate-x-1/2 left-1/2 absolute -bottom-1' />
            </span>
          </button>
        ) : (
          <div
            role='img'
            aria-label={hotelTitle(item.properties.title, item.properties.hotelType)}
            className='rounded-full bg-black aspect-square w-10 border-2 flex items-center justify-center border-white'
          >
            <FLogo className='size-5' aria-hidden='true' />
          </div>
        )}
      </Marker>
    </>
  )
}

export default Markers
